import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { App, type AppProps } from "./App";
import { ChatIcon, CloseIcon } from "./components/icons";
import { useIsSheet } from "./useViewport";

/**
 * Floating launcher (bottom-right / bottom-left). The App stays mounted once opened so a live conversation keeps
 * running while minimised (bubble shows a live dot). On phones / short viewports the panel opens as a full-screen sheet.
 */
export function Popup(props: Omit<AppProps, "onLiveChange" | "popup"> & { openOnLoad?: boolean | undefined }) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [live, setLive] = useState(false);
  const [sheet, setSheet] = useState(useIsSheet);
  const panel = useRef<HTMLDivElement>(null);
  const label = props.config.appearance?.launcherLabel?.trim() || "Talk to us";

  useEffect(() => {
    const mq = matchMedia("(max-width: 40em), (max-height: 34em)");
    const onChange = () => setSheet(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  useEffect(() => {
    if (props.openOnLoad) { setMounted(true); setOpen(true); }
  }, [props.openOnLoad]);
  // `inert` keeps the hidden panel out of the tab order / accessibility tree.
  useEffect(() => {
    const el = panel.current;
    if (!el) return;
    if (open) el.removeAttribute("inert"); else el.setAttribute("inert", "");
  }, [open, mounted]);

  const toggle = useCallback(() => {
    setMounted(true);
    setOpen((o) => !o);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const isSheet = sheet && open;

  return (
    <div class={`wk-popup${isSheet ? " sheet-open" : ""}`}>
      <div ref={panel} class={`wk-panel${mounted ? " mounted" : ""}${open ? " open" : ""}${sheet ? " sheet" : ""}`} aria-hidden={!open}>
        {mounted && <App {...props} onLiveChange={setLive} popup={{ onClose: close, open, fullscreen: isSheet }} />}
      </div>
      <button class={`wk-launcher${live ? " live" : ""}`} onClick={toggle} aria-expanded={open} aria-label={open ? "Minimise assistant" : label}>
        <span class="live-dot" aria-hidden="true" />
        {open ? <CloseIcon /> : <ChatIcon />}
        {!open && <span class="wk-launcher-label">{live ? "Conversation running" : label}</span>}
      </button>
    </div>
  );
}
